import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import profileIcon from "src/assets/svg-icons/profile.svg";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import emailIcon from "src/assets/svg-icons/email.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import EditIcon from "src/assets/svg-icons/aggregators/edit.svg";
import { useEffect, useRef, useState } from "react";
import LockDatePopup from "./lock-date-popup";
import moment from "moment";
import { oneBranchHasPermission } from "src/utils";
import { SettingsPermissions } from "src/constants/permissions";
import GovernmentTaxPopup from "./government-tax";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import {
  useConnectFoodicsCallback,
  useInitiateConnectToFoodics,
} from "src/api/profile";
import { notificationsState } from "src/store/notifications";
import StartBalanceDatePopup from "./start-balance-date-popup";
import InfoItem from "./info-item";
import EditInfoPopup from "./edit-info-popup";

const MyProfile = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("profile");
  const { t: generalT } = useTranslation("general");
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const [auth, setAuth] = useRecoilState(authState);
  const { user } = auth;
  const company = user?.company;
  const [open, setOpen] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [openFoodics, setOpenFoodics] = useState(false);
  const [openBalanceDate, setOpenBalanceDate] = useState(false);
  const [openGovTax, setOpenGovTax] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [isLoading, setIsLoading] = useState(false);
  const calledRef = useRef(false);

  const initials = user?.name
    .split(" ")
    .map((word: string) => word.charAt(0))
    .join("");

  // apis
  const { data, refetch, isFetching, status, error } =
    useInitiateConnectToFoodics();
  const {
    mutate,
    isPending,
    error: callBackError,
    status: callBackStatus,
  } = useConnectFoodicsCallback();

  // permissions
  const editCompanyLockDatePermission = oneBranchHasPermission(
    SettingsPermissions.companyLockDate,
  );

  const navigateToDashboard = () => {
    navigate("/dashboard");
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenInfo = () => setOpenInfo(true);
  const handleCloseInfo = () => setOpenInfo(false);

  const handleOpenFoodics = () => setOpenFoodics(true);
  const handleCloseFoodics = () => setOpenFoodics(false);

  const handleOpenGovTax = () => setOpenGovTax(true);
  const handleCloseGovTax = () => setOpenGovTax(false);

  const handleConnectToFoodics = () => {
    setIsLoading(true);
    refetch();
  };

  useEffect(() => {
    if (!code || !state || calledRef.current) return;

    calledRef.current = true;
    mutate({ code, state });
  }, [code, state]);

  //Connect to foodics success and error handling
  useEffect(() => {
    if (status === "success") {
      window.location.href = data?.data?.authorization_url;
      // handleClose();
    } else if (status === "error") {
      setIsLoading(false);
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value ?? generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Callback success and error handling
  useEffect(() => {
    if (callBackStatus === "success") {
      setSearchParams();
      // window.location.reload();
      const newAuth = {
        ...auth,
        user: {
          ...user,
          company: {
            ...user.company,
            foodics_connected: true,
          },
        },
      };
      setAuth(newAuth);
    } else if (callBackStatus === "error") {
      setSearchParams();
      window.location.reload();
    }
  }, [callBackStatus]);

  const handleOpenBalance = () => setOpenBalanceDate(true);
  const handleCloseBalance = () => setOpenBalanceDate(false);

  return (
    <Stack gap={3} p={{ xs: 2, sm: 3, md: "30px 50px", lg: "30px 200px" }}>
      <Stack direction={"row"} spacing={"12px"} alignItems={"center"}>
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={profileIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"}>
          {t("myProfile")}
        </Typography>
      </Stack>

      <Stack spacing={2} direction={"row"} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToDashboard}
          sx={{ cursor: "pointer" }}
        >
          {t("home")}
        </Typography>
        <img
          src={RightArrow}
          alt=""
          style={{
            transform: i18n.language === "en" ? "none" : "scaleX(-1)",
          }}
        />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {t("profile")}
          </Typography>
        </Box>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />
      {isPending ? (
        <Box display={"flex"} justifyContent={"center"} pt={13}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={3}
            bgcolor={"#F9FAFB"}
            borderRadius={"8px 8px 0px 0px"}
            p={2}
          >
            <Avatar
              sx={{ borderRadius: 1, width: "140px", height: "140px" }}
              variant="square"
            >
              {/* <Typography textTransform={"uppercase"}>{initials}</Typography> */}
            </Avatar>
            <Box>
              <Typography
                fontSize={"23px"}
                fontWeight={600}
                lineHeight={"27.6px"}
              >
                {user.name}
              </Typography>
              <Stack direction={"row"} spacing={1}>
                <img src={emailIcon} width={"20px"} alt="" />

                <Typography
                  fontSize={"16px"}
                  color={"#98A2B3"}
                  lineHeight={"19.2px"}
                >
                  {user.email}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack
            borderRadius={"0px 0px 8px 8px"}
            border={"1px solid #EAECF0"}
            bgcolor={"#FFF"}
            p={3}
            spacing={"12px"}
          >
            <Typography
              align="center"
              fontSize={"16px"}
              color={"#475467"}
              lineHeight={"19.2px"}
            >
              {t("profileDetails")}
            </Typography>
            {/* divider */}
            <Box bgcolor={"#EAECF0"} width={"100%"} height={"1px"} />

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Grid
                  container
                  bgcolor={"#F9FAFB"}
                  height={"100%"}
                  p={2}
                  spacing={2}
                >
                  <InfoItem label={t("fullName")} value={user.name ?? "--"} />

                  <InfoItem label={t("email")} value={user.email ?? "--"} />

                  <InfoItem
                    label={t("company")}
                    value={user.company.name ?? "--"}
                  />

                  <InfoItem
                    label={t("companyId")}
                    value={user.company.reference_number ?? "--"}
                  />

                  <InfoItem
                    label={generalT("lockDate")}
                    value={
                      <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Typography
                          fontSize={{ xs: "12", lg: "16px" }}
                          fontWeight={600}
                          lineHeight={"19.2px"}
                          color={"#101828"}
                        >
                          {moment(user.company?.close_date)?.format(
                            "DD/MM/YYYY",
                          )}
                        </Typography>
                        {editCompanyLockDatePermission ? (
                          <IconButton sx={{ p: "0" }} onClick={handleOpen}>
                            <img src={EditIcon} alt="" />
                          </IconButton>
                        ) : null}
                      </Stack>
                    }
                  />

                  <InfoItem
                    label={generalT("startBalanceDate")}
                    value={
                      <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Typography
                          fontSize={{ xs: "12", lg: "16px" }}
                          fontWeight={600}
                          lineHeight={"19.2px"}
                          color={"#101828"}
                        >
                          {user.company?.start_balance_date
                            ? moment(user.company?.start_balance_date)?.format(
                                "DD/MM/YYYY",
                              )
                            : "--"}
                        </Typography>
                        <IconButton sx={{ p: "0" }} onClick={handleOpenBalance}>
                          <img src={EditIcon} alt="" />
                        </IconButton>
                      </Stack>
                    }
                  />

                  <InfoItem
                    label={t("governmentStartBalanceTax")}
                    value={
                      <Stack direction={"row"} gap={1} alignItems={"center"}>
                        <Typography
                          fontSize={{ xs: "12", lg: "16px" }}
                          fontWeight={600}
                          lineHeight={"19.2px"}
                          color={"#101828"}
                        >
                          {user.company?.government_tax}
                        </Typography>
                        {editCompanyLockDatePermission ? (
                          <IconButton
                            sx={{ p: "0" }}
                            onClick={handleOpenGovTax}
                          >
                            <img src={EditIcon} alt="" />
                          </IconButton>
                        ) : null}
                      </Stack>
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container bgcolor={"#F9FAFB"} p={2} spacing={2}>
                  <InfoItem
                    label={t("taxRegistrationNumber")}
                    value={company.tax_registration_number ?? "--"}
                  />
                  <InfoItem
                    label={t("commercialRegistrationNumber")}
                    value={company.commercial_registration_number ?? "--"}
                  />
                  <InfoItem
                    label={t("phoneNumber")}
                    value={company.address?.phone ?? "--"}
                  />
                  <InfoItem
                    label={t("secondaryNumber")}
                    value={company.address?.secondary_number ?? "--"}
                  />
                  <InfoItem
                    label={t("streetAddress")}
                    value={company.address?.street_address ?? "--"}
                  />
                  <InfoItem
                    label={t("buildingNumber")}
                    value={company.address?.building_number ?? "--"}
                  />
                  <InfoItem
                    label={t("district")}
                    value={company.address?.district ?? "--"}
                  />
                  <InfoItem
                    label={t("city")}
                    value={company.address?.city?.name ?? "--"}
                  />
                  <InfoItem
                    label={t("country")}
                    value={company.address?.country?.name ?? "--"}
                  />
                  <InfoItem
                    label={t("postalCode")}
                    value={company.address?.postal_code ?? "--"}
                  />
                  <Box
                    mt={1}
                    width={"100%"}
                    display={"flex"}
                    justifyContent={"flex-end"}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleOpenInfo}
                    >
                      Edit info
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      )}

      {open && <LockDatePopup open={open} handleClose={handleClose} />}

      {openGovTax && (
        <GovernmentTaxPopup open={openGovTax} handleClose={handleCloseGovTax} />
      )}

      {openBalanceDate && (
        <StartBalanceDatePopup
          open={openBalanceDate}
          handleClose={handleCloseBalance}
        />
      )}

      {openGovTax && (
        <GovernmentTaxPopup open={openGovTax} handleClose={handleCloseGovTax} />
      )}

      {openInfo && (
        <EditInfoPopup open={openInfo} handleClose={handleCloseInfo} />
      )}

      <ConfirmPopup
        open={openFoodics}
        handleClose={handleCloseFoodics}
        handleConfirm={handleConnectToFoodics}
        title={" "}
        subtitle={t("sureToConnectToFoodics")}
        confirmBtnText={generalT("confirm")}
        noIcon
        confirmBtnColor={"primary"}
        isPending={isLoading}
      />
    </Stack>
  );
};

export default MyProfile;
